import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import adminClient from '@/lib/api/adminClient';
import { formatDate } from '@/lib/utils';

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  summary: string;
  language: string;
  category: string | null;
  difficulty: string | null;
  is_published: boolean;
  published_at: string | null;
  author: string;
  tags: string | null;
}

const EMPTY_FORM = {
  slug: '', title: '', summary: '', content: '', cover_image_url: '',
  author: 'Sentinel Team', tags: '', language: 'zh-TW',
  category: '', difficulty: '', is_published: false, published_at: '',
};

export function BlogPage() {
  const qc = useQueryClient();
  const [langFilter, setLangFilter] = useState('all');
  const [pubFilter, setPubFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [fetchContent, setFetchContent] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'blog', page, langFilter, pubFilter],
    queryFn: () =>
      adminClient.get('/admin/blog', {
        params: {
          page,
          per_page: 20,
          language: langFilter !== 'all' ? langFilter : undefined,
          is_published: pubFilter === 'all' ? undefined : pubFilter === 'published',
        },
      }).then((r) => r.data),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFetchContent('');
    setDialogOpen(true);
  };

  const openEdit = async (post: BlogPost) => {
    setEditing(post);
    // Fetch full content via admin endpoint (supports drafts)
    const res = await adminClient.get(`/admin/blog/${post.slug}`).catch(() => null);
    const content = res?.data?.content ?? '';
    setFetchContent(content);
    setForm({
      slug: post.slug,
      title: post.title,
      summary: post.summary,
      content,
      cover_image_url: '',
      author: post.author,
      tags: post.tags ?? '',
      language: post.language,
      category: post.category ?? '',
      difficulty: post.difficulty ?? '',
      is_published: post.is_published,
      published_at: post.published_at ?? '',
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        cover_image_url: form.cover_image_url || undefined,
        category: form.category || undefined,
        difficulty: form.difficulty || undefined,
        tags: form.tags || undefined,
        published_at: form.published_at || undefined,
      };
      if (editing) {
        return adminClient.put(`/blog/${editing.slug}`, payload);
      }
      return adminClient.post('/blog', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blog'] });
      setDialogOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => adminClient.delete(`/blog/${slug}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blog'] });
      setDeleteSlug(null);
    },
  });

  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Blog 管理</h1>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />新增文章</Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={langFilter} onValueChange={(v) => { setLangFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="語言" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部語言</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="zh-TW">繁體中文</SelectItem>
            <SelectItem value="zh-CN">简体中文</SelectItem>
            <SelectItem value="ko">한국어</SelectItem>
            <SelectItem value="ja">日本語</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pubFilter} onValueChange={(v) => { setPubFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="狀態" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部</SelectItem>
            <SelectItem value="published">已發布</SelectItem>
            <SelectItem value="draft">草稿</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>標題</TableHead>
                  <TableHead>語言</TableHead>
                  <TableHead>分類</TableHead>
                  <TableHead>難度</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>發布日期</TableHead>
                  <TableHead className="w-[100px]">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((post: BlogPost) => (
                  <TableRow key={post.id}>
                    <TableCell className="max-w-[280px]">
                      <p className="font-medium truncate">{post.title}</p>
                      <p className="text-xs text-muted-foreground font-mono">{post.slug}</p>
                    </TableCell>
                    <TableCell><Badge variant="outline">{post.language}</Badge></TableCell>
                    <TableCell className="text-sm">{post.category ?? '—'}</TableCell>
                    <TableCell className="text-sm">{post.difficulty ?? '—'}</TableCell>
                    <TableCell>
                      {post.is_published
                        ? <Badge variant="success" className="flex items-center gap-1 w-fit"><Eye className="w-3 h-3" />已發布</Badge>
                        : <Badge variant="secondary" className="flex items-center gap-1 w-fit"><EyeOff className="w-3 h-3" />草稿</Badge>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {post.published_at ? formatDate(post.published_at) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(post)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteSlug(post.slug)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一頁</Button>
          <span className="text-sm text-muted-foreground">第 {page} 頁，共 {Math.ceil(data.total / 20)} 頁（{data.total} 篇）</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage(page + 1)}>下一頁</Button>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? '編輯文章' : '新增文章'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-1 col-span-2">
              <label className="text-sm font-medium">標題</label>
              <Input value={form.title} onChange={f('title')} placeholder="文章標題" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Slug</label>
              <Input value={form.slug} onChange={f('slug')} placeholder="my-article-slug" disabled={!!editing} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">作者</label>
              <Input value={form.author} onChange={f('author')} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">語言</label>
              <Select value={form.language} onValueChange={(v) => setForm((p) => ({ ...p, language: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zh-TW">繁體中文</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="zh-CN">简体中文</SelectItem>
                  <SelectItem value="ko">한국어</SelectItem>
                  <SelectItem value="ja">日本語</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">分類</label>
              <Select value={form.category || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, category: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="選擇分類" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">無</SelectItem>
                  <SelectItem value="tutorial">Tutorial</SelectItem>
                  <SelectItem value="strategy">Strategy</SelectItem>
                  <SelectItem value="tool-review">Tool Review</SelectItem>
                  <SelectItem value="career">Career</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">難度</label>
              <Select value={form.difficulty || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, difficulty: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="選擇難度" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">無</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Tags（逗號分隔）</label>
              <Input value={form.tags} onChange={f('tags')} placeholder="rsi,backtest,tutorial" />
            </div>
            <div className="space-y-1 col-span-2">
              <label className="text-sm font-medium">封面圖片 URL</label>
              <Input value={form.cover_image_url} onChange={f('cover_image_url')} placeholder="https://..." />
            </div>
            <div className="space-y-1 col-span-2">
              <label className="text-sm font-medium">摘要（120-160 字元最佳）</label>
              <textarea
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.summary}
                onChange={f('summary')}
                placeholder="文章摘要..."
              />
              <p className="text-xs text-muted-foreground text-right">{form.summary.length} 字元</p>
            </div>
            <div className="space-y-1 col-span-2">
              <label className="text-sm font-medium">內文（Markdown）</label>
              <textarea
                className="w-full h-56 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.content}
                onChange={f('content')}
                placeholder="# 文章標題&#10;&#10;正文..."
              />
            </div>
            <div className="flex items-center gap-3 col-span-2">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => setForm((p) => ({ ...p, is_published: v }))}
                id="is-published"
              />
              <label htmlFor="is-published" className="text-sm font-medium">立即發布</label>
            </div>
          </div>
          {saveMutation.isError && (
            <p className="text-sm text-destructive px-4">{(saveMutation.error as any)?.response?.data?.detail || '儲存失敗'}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.title || !form.slug}>
              {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? '儲存變更' : '建立文章'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteSlug} onOpenChange={() => setDeleteSlug(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>確認刪除</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-4">確定要刪除文章 <code className="text-foreground font-mono">{deleteSlug}</code>？此操作無法還原。</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSlug(null)}>取消</Button>
            <Button variant="destructive" onClick={() => deleteSlug && deleteMutation.mutate(deleteSlug)} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              刪除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
